/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Welcome", description = "Learn what Hightouch does, choose the right starting point, and find setup guidance for your team.", lastUpdatedDate = new Date(1784678400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Welcome",
        description: "Learn what Hightouch does, choose the right starting point, and find setup guidance for your team.",
        lastUpdatedDate: "Wed Jul 22 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What is Hightouch?",
          "depth": 0,
          "id": "what-is-hightouch"
        }, {
          "value": "Find the right starting point",
          "depth": 0,
          "id": "find-the-right-starting-point"
        }, {
          "value": "Explore products",
          "depth": 0,
          "id": "explore-products"
        }, {
          "value": "Marketing",
          "depth": 1,
          "id": "marketing"
        }, {
          "value": "Data platform",
          "depth": 1,
          "id": "data-platform"
        }, {
          "value": "Get started by team",
          "depth": 0,
          "id": "get-started-by-team"
        }, {
          "value": "Understand how Hightouch works",
          "depth": 0,
          "id": "understand-how-hightouch-works"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      a: "a",
      h2: "h2",
      p: "p",
      hr: "hr",
      h3: "h3",
      ul: "ul",
      li: "li",
      strong: "strong",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components), {WhatsNew, WhatsNewItem, UseCaseGrid, UseCaseCard, ProductFinder} = _components;
    if (!ProductFinder) _missingMdxReference("ProductFinder", true);
    if (!UseCaseCard) _missingMdxReference("UseCaseCard", true);
    if (!UseCaseGrid) _missingMdxReference("UseCaseGrid", true);
    if (!WhatsNew) _missingMdxReference("WhatsNew", true);
    if (!WhatsNewItem) _missingMdxReference("WhatsNewItem", true);
    return _jsxs(_Fragment, {
      children: [_jsx(WhatsNew, {
        children: _jsxs(WhatsNewItem, {
          children: ["Search and read the docs from Claude, Cursor, and other AI tools with the new ", _jsx(_components.a, {
            href: "/developer-tools/agent-resources#mcp-server",
            children: "docs MCP server"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "what-is-hightouch",
        children: "What is Hightouch?"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch is an Agentic Composable CDP for marketing and personalization."
      }), "\n", _jsx(_components.p, {
        children: "It runs on top of your data warehouse, so teams can use customer data without copying it into a separate system. Data teams define and govern datasets at the source. Marketers use those datasets to build audiences, run campaigns, personalize experiences, and measure results."
      }), "\n", _jsxs(_components.p, {
        children: ["Agents are built into Hightouch to help marketers explore data, build audiences, generate creative, and analyze campaign performance. ", _jsx(_components.a, {
          href: "/ai-decisioning/overview",
          children: "AI Decisioning"
        }), " uses reinforcement learning to recommend the best message, channel, and timing for each customer. Marketers review, refine, and approve AI outputs before taking action."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "find-the-right-starting-point",
        children: "Find the right starting point"
      }), "\n", _jsx(_components.p, {
        children: "Start with what you want to do. Each path points you to the product area and setup guide that best matches your goal."
      }), "\n", _jsxs(UseCaseGrid, {
        columns: 2,
        children: [_jsx(UseCaseCard, {
          title: "Build audiences and explore data with AI",
          href: "/customer-studio/overview",
          icon: "audiences",
          destination: "Explore Customer Studio",
          children: _jsx(_components.p, {
            children: "Create audiences from warehouse data, build automated journeys, and use the built-in agent to explore segments and analyze campaign performance in natural language."
          })
        }), _jsx(UseCaseCard, {
          title: "Sync warehouse data to business tools",
          href: "/getting-started/concepts",
          icon: "sync",
          destination: "Explore data activation",
          children: _jsx(_components.p, {
            children: "Sync modeled data to sales, support, analytics, and internal tools on a schedule or when data changes."
          })
        }), _jsx(UseCaseCard, {
          title: "Optimize message, channel, and timing",
          href: "/ai-decisioning/overview",
          icon: "ai",
          destination: "Explore AI Decisioning",
          children: _jsx(_components.p, {
            children: "AI Decisioning uses reinforcement learning to recommend the best message, channel, and timing for each customer, then measures what worked."
          })
        }), _jsx(UseCaseCard, {
          title: "Personalize in real time",
          href: "/real-time/overview",
          icon: "realtime",
          destination: "Explore Real-Time Personalization",
          children: _jsx(_components.p, {
            children: "Tailor website and app experiences based on what a customer is doing right now."
          })
        })]
      }), "\n", _jsxs(UseCaseGrid, {
        columns: 3,
        children: [_jsx(UseCaseCard, {
          title: "Unify customer profiles",
          href: "https://hightouch.com/platform/identity-resolution",
          variant: "secondary",
          icon: "identity",
          destination: "Explore Identity Resolution",
          children: _jsx(_components.p, {
            children: "Resolve identities across devices and channels so teams can build on a more complete customer record."
          })
        }), _jsx(UseCaseCard, {
          title: "Set up governance and safe self-serve",
          href: "/workspace-management/governance",
          variant: "secondary",
          icon: "governance",
          destination: "Explore governance",
          children: _jsx(_components.p, {
            children: "Set up permissions, approvals, and ownership boundaries so each team has the right level of access."
          })
        }), _jsx(UseCaseCard, {
          title: "Increase ad match rates",
          href: "/match-booster/overview",
          variant: "secondary",
          icon: "matchbooster",
          destination: "Explore Match Booster",
          children: _jsx(_components.p, {
            children: "Help ad platforms recognize more of your customers by adding additional identifiers to synced audiences and conversions."
          })
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(ProductFinder, {}), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Not sure where to start? Read ", _jsx(_components.a, {
            href: "/getting-started/concepts",
            children: "How data activation works"
          }), " to learn the technical foundations, or see ", _jsx(_components.a, {
            href: "/getting-started/business",
            children: "Daily usage for marketers"
          }), " for the marketing workflow from audiences to measurement."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "explore-products",
        children: "Explore products"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch products fall into two groups. You can start with one and expand over time — they all work from the same warehouse data."
      }), "\n", _jsx(_components.h3, {
        id: "marketing",
        children: "Marketing"
      }), "\n", _jsx(_components.p, {
        children: "Build audiences, create campaigns, and measure results — with AI agents that help at every step."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/customer-studio/overview",
              children: "Customer Studio"
            })
          }), " — Build audiences, run journeys, and use the built-in agent to explore data in natural language"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "https://hightouch.com/platform/agentic-marketing-platform",
              children: "Agents"
            })
          }), " — Ask questions, analyze campaigns, and draft reports grounded in your warehouse data"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "https://hightouch.com/platform/agentic-marketing-platform",
              children: "Context Hub"
            })
          }), " — Configure brand context, channel connections, and review settings shared across Agents, Ad Studio, and Lifecycle Studio"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "https://hightouch.com/platform/ad-studio",
              children: "Ad Studio"
            })
          }), " — Draft and refine ad creatives, then export to ad platforms"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/ai-decisioning/overview",
              children: "AI Decisioning"
            })
          }), " — Recommend the best message, channel, and timing for each customer using reinforcement learning"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/campaign-intelligence/overview",
              children: "Intelligence"
            })
          }), " — Run experiments, compare results, and track campaign metrics"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "data-platform",
        children: "Data platform"
      }), "\n", _jsx(_components.p, {
        children: "Infrastructure for collecting data, modeling it, delivering it to downstream tools, and powering real-time experiences."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/models/creating-models",
              children: "Models"
            })
          }), " — Define reusable datasets with SQL, dbt, or a visual builder"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/syncs/overview",
              children: "Syncs"
            })
          }), " — Deliver data to destinations on a schedule or when data changes"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/events/overview",
              children: "Hightouch Events"
            })
          }), " — Collect behavioral data from apps and websites"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/real-time/overview",
              children: "Real-Time Personalization"
            })
          }), " — Personalize onsite and in-app experiences"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "https://hightouch.com/platform/identity-resolution",
              children: "Identity Resolution"
            })
          }), " — Unify customer profiles across devices and channels"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/match-booster/overview",
              children: "Match Booster"
            })
          }), " — Increase ad match rates with additional identifiers"]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "get-started-by-team",
        children: "Get started by team"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch is a cross-functional platform. Different teams own different parts of setup and daily usage."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Role"
            }), _jsx(_components.th, {
              children: "What you'll set up"
            }), _jsx(_components.th, {
              children: "Start here"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Platform admins"
            }), _jsx(_components.td, {
              children: "Configure SSO, roles, approval flows, and workspace settings"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/getting-started/it",
                children: "Workspace management"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Data teams"
            }), _jsx(_components.td, {
              children: "Connect data sources, define models, manage syncs, and set up schema"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/getting-started/data",
                children: "Data modeling and access"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Marketers"
            }), _jsx(_components.td, {
              children: "Build audiences, launch campaigns, manage journeys, and measure experiments"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/getting-started/business",
                children: "Daily usage"
              })
            })]
          })]
        })]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "These paths are designed to be followed in order: platform admins set up the workspace, data teams connect and model data, then marketers start building. Each guide tells you what other teams need to have completed before you begin."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "understand-how-hightouch-works",
        children: "Understand how Hightouch works"
      }), "\n", _jsx(_components.p, {
        children: "Before diving into setup, these pages help you build a mental model of the platform:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/getting-started/concepts",
            children: "How data activation works"
          }), " — Sources, models, syncs, destinations, and change data capture: the building blocks underneath everything in Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/getting-started/business",
            children: "Daily usage for marketers"
          }), " — Audiences, agents, journeys, AI Decisioning, and measurement: the marketing workflow from data to results."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/governance",
            children: "Governance best practices"
          }), " — How to structure roles, approval flows, environments, and audit logs so the right people can move fast without creating risk."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
